import {
  Card,
  CardHeader,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Typography
} from '@mui/material';
import {
  DataGrid, gridPageCountSelector,
  gridPageSelector,
  koKR,
  useGridApiContext,
  useGridSelector
} from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { apiService } from '../../api/axiosClient';
import CustomChip from '../../components/mui/chip';
import Spinner from '../../components/spinner';
import CustomNoRowsOverlay from '../../utils/CustomNoRowsOverlay';
import CommunityToolbar from './CommunityToolbar';

const TableCommunity = ({setPostId}) => {
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [sort, setSort] = useState('desc');
  const [pageSize, setPageSize] = useState(10);
  const [list, setList] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [sortColumn, setSortColumn] = useState('id');
  const [selected, setSelected] = useState([]);

  const loadCommunity = async (sort, query, column) => {
        try {
            const response = await apiService.get('/post', {
                params: {
                    state: '',
                    query,
                    sort,
                    column,
                    page,
                    pageSize
                }
            })
            const payload = response.data;
            setList(payload.list);
            setTotal(payload.size);
            setLoading(false)
        } catch (e) {
            console.error(e)
        }
    };

  useEffect(() => {
    loadCommunity(sort, searchValue, sortColumn)
  }, [page, pageSize, reload])

  const handleSortModel = (newModel) => {
    if (newModel.length) {
      setSort(newModel[0].sort)
      setSortColumn(newModel[0].field)
      loadCommunity(newModel[0].sort, searchValue, newModel[0].field)
    } else {
      setSort('desc')
      setSortColumn('uptime')
    }
  }

  const handleSearch = value => {
    setSearchValue(value)
    loadCommunity(sort, value, sortColumn)
  }

  const onPageSizeChange = e => {
    setPageSize(e.target.value)
  }

  const showState = (state) => {
    switch (state) {
      case '10':
        return '답변을 기다리는 질문';
      case '20':
        return '답변';
      default:
        return '전체';
    }
  }

  function CustomPagination() {
    const apiRef = useGridApiContext();
    const _page = useGridSelector(apiRef, gridPageSelector);
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
      <Stack direction="row" spacing={2} sx={{ pr: 4, alignItems: 'center' }}>
        <InputLabel id="pagesize-select">행/페이지</InputLabel>
        <FormControl variant="standard" sx={{ mr: 1, minWidth: 70 }}>
          <Select
            labelId="pagesize-select"
            id="pagesize-select"
            value={pageSize}
            onChange={onPageSizeChange}
            label="pageSize"
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
        </FormControl>
        <Pagination
          color="primary"
          count={pageCount}
          page={_page + 1}
          onChange={(event, value) => apiRef.current.setPage(value - 1)}
        />
      </Stack>
    );
  }

  const columns = [
    {
      minWidth: 100,
      field: 'id',
      headerName: 'ID',
      renderCell: params => (
        <a href='#' onClick={() => setPostId(params.row.id)} >
          <CustomChip
            size='small'
            skin='light'
            color='primary'
            label={params.row.id}
            sx={{ '& .MuiChip-label': { textTransform: 'capitalize' }, padding: 2, cursor: 'pointer' }}
          />
        </a>
      )
    },
    {
      minWidth:300,
      field: 'title',
      headerName: '제목',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.title}
        </Typography>
      )
    },
    {
      minWidth: 500,
      field: 'question',
      headerName: '질의',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.question}
        </Typography>
      )
    },
    {
      minWidth: 100,
      field: 'visit',      
      headerName: '클릭수',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.visit}
        </Typography>
      )
    },
    {
      minWidth: 100,
      field: 'state',      
      headerName: '상태',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {showState(params.row.state)}
        </Typography>
      )
    },
    {
      minWidth: 200,
      field: 'uptime',      
      headerName: '변경날짜',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.uptime.toLocaleString('ko-KR')}
        </Typography>
      )
    }
  ]

  return (
    loading ?
    <Spinner/>
    :
    <Card>
      <CardHeader title='질의 내역' />
      <DataGrid
        sx={{
          ...(list.length == 0 && { '& .MuiDataGrid-virtualScrollerContent': {height: '200px !important' } })
        }}
        localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
        autoHeight
        getRowId={row => row.id}
        pagination
        rows={list}
        rowCount={total || 0}
        columns={columns}
        pageSize={pageSize}
        sortingMode='server'
        paginationMode='server'
        onSortModelChange={handleSortModel}
        onPageChange={newPage => setPage(newPage)}
        components={{
          Toolbar: CommunityToolbar,
          Pagination: CustomPagination,
          NoRowsOverlay: CustomNoRowsOverlay,
        }}
        componentsProps={{
          toolbar: {
            value: searchValue,
            clearSearch: () => handleSearch(''),
            onChange: event => handleSearch(event.target.value),
            numSelected: selected.length,
            selected,
            setReload
          }
        }}
        checkboxSelection
        disableSelectionOnClick
        onSelectionModelChange={(ids) => {
          const selectedIDs = new Set(ids);
          const selectedRows = list.filter((row) =>
            selectedIDs.has(row.id),
          );

          setSelected(selectedRows)
        }}
        experimentalFeatures={{ newEditingApi: true }}
      />
    </Card>
  )
}

export default TableCommunity
